/**
 * 敏感信息（手机号/身份证/银行卡/营业执照等）全局脱敏、隐藏工具
 *
 * 用途：三级等保要求后台不能明文展示用户敏感信息。
 * 通过全局开关（设置抽屉 -> 安全设置）控制：
 *   关闭：原样展示（默认，不影响日常运营编辑）
 *   开启：按字段类型脱敏（138****8888）或完全隐藏（******）
 *
 * 生效方式：
 *   1. 全局兜底：utils/request.js 响应拦截器中调用 maskData()，所有接口返回数据自动生效
 *   2. 细粒度：页面可使用 <sensitive-text :value="x" field="mobile" /> 或 this.$sensitive(value, type)
 */
import storage from 'store'
import { SENSITIVE_HIDDEN, SENSITIVE_MODE } from '@/store/mutation-types'

/** 敏感字段类型 */
export const SENSITIVE_TYPE = {
  PHONE: 'phone',
  ID_CARD: 'idCard',
  BANK_CARD: 'bankCard',
  LICENSE: 'license',
  NAME: 'name',
  EMAIL: 'email',
  ADDRESS: 'address',
  DEFAULT: 'default'
}

/** 服务端参数配置 sys_config 的键名（后台 -> 系统管理 -> 参数设置） */
export const SENSITIVE_CONFIG_KEY_HIDDEN = 'sys.sensitive.hidden'
export const SENSITIVE_CONFIG_KEY_MODE = 'sys.sensitive.mode'

/** 展示模式：mask-脱敏（138****8888） hide-隐藏（******） */
export const SENSITIVE_MODE_MASK = 'mask'
export const SENSITIVE_MODE_HIDE = 'hide'

/** 隐藏模式下的占位符 */
const HIDE_PLACEHOLDER = '******'

/**
 * 字段名匹配规则（按数组顺序优先匹配，全部为「转小写后包含」匹配）
 * 注意顺序：银行卡 -> 营业执照 -> 身份证 -> 手机号 -> 邮箱 -> 姓名 -> 地址
 */
const FIELD_RULES = [
  {
    type: SENSITIVE_TYPE.BANK_CARD,
    keywords: ['bankcard', 'bankno', 'bankaccount', 'banknumber', 'banknum', 'accountno', 'accountnumber', 'settleaccount', 'cardnumber']
  },
  {
    type: SENSITIVE_TYPE.LICENSE,
    keywords: ['businesslicense', 'licenseno', 'licensenumber', 'licensenum', 'licence', 'license']
  },
  {
    type: SENSITIVE_TYPE.ID_CARD,
    keywords: ['idcard', 'idno', 'identity', 'idnumber', 'idnum', 'certificateno', 'certno', 'cardno', 'socialno']
  },
  {
    type: SENSITIVE_TYPE.PHONE,
    keywords: ['phone', 'mobile', 'telephone', 'contacttel', 'linktel', 'contactnumber']
  },
  {
    type: SENSITIVE_TYPE.EMAIL,
    keywords: ['email', 'mailbox']
  },
  {
    type: SENSITIVE_TYPE.NAME,
    keywords: ['realname', 'truename', 'legalperson', 'legalname', 'contactperson', 'contacts', 'payeename']
  },
  {
    type: SENSITIVE_TYPE.ADDRESS,
    keywords: ['address', 'addr']
  }
]

/** 是否为图片/文件地址（图片地址不属于字符型敏感信息，跳过处理，避免打码后无法展示） */
function isUrl (str) {
  if (/^(https?:\/\/|\/\/|data:|blob:)/i.test(str)) {
    return true
  }
  return /^[\w\-./\\]+\.(jpg|jpeg|png|gif|webp|bmp|svg|mp4|avi|mov|pdf|doc|docx|xls|xlsx)$/i.test(str)
}

/** 手机号：138****8888 */
function maskPhone (str) {
  if (/^\d{11}$/.test(str)) {
    return str.substr(0, 3) + '****' + str.substr(7)
  }
  if (/^\d{7,20}$/.test(str)) {
    return keepHeadTail(str, 3, 2)
  }
  return keepHeadTail(str, 2, 2)
}

/** 身份证：110***********1234 */
function maskIdCard (str) {
  if (/^\d{17}[\dXx]$/.test(str) || /^\d{15}$/.test(str)) {
    return str.substr(0, 3) + '***********' + str.substr(-4)
  }
  return keepHeadTail(str, 2, 2)
}

/** 银行卡：**** **** **** 1234 */
function maskBankCard (str) {
  const pure = str.replace(/\s/g, '')
  if (/^\d{12,25}$/.test(pure)) {
    return '**** **** **** ' + pure.substr(-4)
  }
  return keepHeadTail(pure, 2, 2)
}

/** 营业执照/统一社会信用代码：9133**********1234 */
function maskLicense (str) {
  if (/^[0-9A-Za-z]{15,25}$/.test(str)) {
    return str.substr(0, 4) + '**********' + str.substr(-4)
  }
  return keepHeadTail(str, 2, 2)
}

/** 姓名：张* / 张*三 */
function maskName (str) {
  const len = str.length
  if (len <= 1) {
    return str
  }
  if (len === 2) {
    return str.substr(0, 1) + '*'
  }
  return str.substr(0, 1) + '*'.repeat(len - 2) + str.substr(-1)
}

/** 邮箱：a***@qq.com */
function maskEmail (str) {
  const atIndex = str.indexOf('@')
  if (atIndex <= 0) {
    return keepHeadTail(str, 2, 2)
  }
  const local = str.substring(0, atIndex)
  const domain = str.substring(atIndex)
  if (local.length <= 1) {
    return '*' + domain
  }
  return local.substr(0, 1) + '***' + domain
}

/** 地址：广东省深圳市**** */
function maskAddress (str) {
  if (str.length <= 8) {
    return str.substr(0, Math.min(2, str.length)) + '****'
  }
  return str.substr(0, 6) + '****'
}

/** 通用兜底：保留前 n 位和后 m 位，中间打码 */
function keepHeadTail (str, head, tail) {
  const len = str.length
  if (len <= head + tail) {
    return '*'.repeat(len)
  }
  return str.substr(0, head) + '****' + str.substr(len - tail)
}

/** 通用脱敏（未知类型兜底，保证绝不泄露明文） */
function maskDefault (str) {
  const len = str.length
  if (len <= 2) {
    return '*'.repeat(len)
  }
  if (len <= 5) {
    return str.substr(0, 1) + '***'
  }
  return keepHeadTail(str, 2, 2)
}

/**
 * 按类型对单个值脱敏
 * @param {*} value 原始值
 * @param {string} type SENSITIVE_TYPE
 * @param {string} mode mask-脱敏 hide-完全隐藏
 */
export function maskValue (value, type, mode) {
  if (value === null || value === undefined || value === '') {
    return value
  }
  if (typeof value !== 'string' && typeof value !== 'number') {
    return value
  }
  const currentMode = mode || getSensitiveMode()
  if (currentMode === SENSITIVE_MODE_HIDE) {
    return HIDE_PLACEHOLDER
  }
  const str = String(value).trim()
  if (!str) {
    return value
  }
  // 图片/文件地址不处理
  if (isUrl(str)) {
    return value
  }
  switch (type) {
    case SENSITIVE_TYPE.PHONE:
      return maskPhone(str)
    case SENSITIVE_TYPE.ID_CARD:
      return maskIdCard(str)
    case SENSITIVE_TYPE.BANK_CARD:
      return maskBankCard(str)
    case SENSITIVE_TYPE.LICENSE:
      return maskLicense(str)
    case SENSITIVE_TYPE.NAME:
      return maskName(str)
    case SENSITIVE_TYPE.EMAIL:
      return maskEmail(str)
    case SENSITIVE_TYPE.ADDRESS:
      return maskAddress(str)
    default:
      return maskDefault(str)
  }
}

/**
 * 根据字段名识别敏感类型
 * @param {string} fieldName 字段名（驼峰/下划线均可）
 * @returns {string|null} SENSITIVE_TYPE 或 null（非敏感字段）
 */
export function matchFieldType (fieldName) {
  if (!fieldName || typeof fieldName !== 'string') {
    return null
  }
  const key = fieldName.toLowerCase().replace(/[_\-\s]/g, '')
  // 以 tel / mobile / phone 结尾的字段名（避免误伤 hotel 等）
  if (/(tel|mobile|phone)$/.test(key)) {
    return SENSITIVE_TYPE.PHONE
  }
  for (let i = 0; i < FIELD_RULES.length; i++) {
    const rule = FIELD_RULES[i]
    for (let j = 0; j < rule.keywords.length; j++) {
      if (key.indexOf(rule.keywords[j]) > -1) {
        return rule.type
      }
    }
  }
  return null
}

/** 读取全局开关状态 */
export function getSensitiveHidden () {
  return storage.get(SENSITIVE_HIDDEN) === true
}

/** 写入全局开关状态 */
export function setSensitiveHidden (hidden) {
  storage.set(SENSITIVE_HIDDEN, hidden === true)
}

/** 读取展示模式 */
export function getSensitiveMode () {
  return storage.get(SENSITIVE_MODE) === SENSITIVE_MODE_HIDE ? SENSITIVE_MODE_HIDE : SENSITIVE_MODE_MASK
}

/** 写入展示模式 */
export function setSensitiveMode (mode) {
  storage.set(SENSITIVE_MODE, mode === SENSITIVE_MODE_HIDE ? SENSITIVE_MODE_HIDE : SENSITIVE_MODE_MASK)
}

/** 递归处理的最大深度，防止异常数据导致死循环 */
const MAX_DEPTH = 12

/**
 * 递归对接口返回数据中的敏感字段进行脱敏（原地修改）
 * 开关关闭时直接返回，无任何性能损耗
 * @param {*} data 任意对象/数组
 * @param {number} depth 当前深度
 * @param {WeakSet} cache 已处理对象，防止循环引用
 */
export function maskData (data, depth, cache) {
  if (!getSensitiveHidden()) {
    return data
  }
  if (data === null || data === undefined || typeof data !== 'object') {
    return data
  }
  if (depth > MAX_DEPTH) {
    return data
  }
  const seen = cache || new WeakSet()
  if (seen.has(data)) {
    return data
  }
  seen.add(data)

  if (Array.isArray(data)) {
    for (let i = 0; i < data.length; i++) {
      const item = data[i]
      if (item !== null && typeof item === 'object') {
        maskData(item, depth + 1, seen)
      }
    }
    return data
  }

  const keys = Object.keys(data)
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]
    const val = data[key]
    if (val === null || val === undefined) {
      continue
    }
    const valueType = typeof val
    if (valueType === 'string' || valueType === 'number') {
      const type = matchFieldType(key)
      if (type) {
        data[key] = maskValue(val, type)
      }
    } else if (valueType === 'object') {
      maskData(val, depth + 1, seen)
    }
  }
  return data
}

/** 脱敏占位特征：连续 3 个及以上 * */
const MASKED_PATTERN = /\*{3,}/

/**
 * 检测提交参数中是否存在「已脱敏的敏感字段」，防止脱敏数据被回写数据库
 * @param {*} data 请求参数
 * @param {number} depth 当前深度
 * @param {WeakSet} cache 已检测对象
 * @returns {string} 命中的字段名，未命中返回空字符串
 */
export function findMaskedSensitiveField (data, depth, cache) {
  if (!getSensitiveHidden()) {
    return ''
  }
  if (data === null || data === undefined || typeof data !== 'object') {
    return ''
  }
  if (depth > MAX_DEPTH) {
    return ''
  }
  const seen = cache || new WeakSet()
  if (seen.has(data)) {
    return ''
  }
  seen.add(data)

  if (Array.isArray(data)) {
    for (let i = 0; i < data.length; i++) {
      const hit = findMaskedSensitiveField(data[i], depth + 1, seen)
      if (hit) {
        return hit
      }
    }
    return ''
  }

  const keys = Object.keys(data)
  for (let i = 0; i < keys.length; i++) {
    const key = keys[i]
    const val = data[key]
    if (val === null || val === undefined) {
      continue
    }
    const valueType = typeof val
    if (valueType === 'string') {
      if (MASKED_PATTERN.test(val) && matchFieldType(key)) {
        return key
      }
    } else if (valueType === 'object') {
      const hit = findMaskedSensitiveField(val, depth + 1, seen)
      if (hit) {
        return hit
      }
    }
  }
  return ''
}

export default {
  SENSITIVE_TYPE,
  maskValue,
  matchFieldType,
  findMaskedSensitiveField,
  getSensitiveHidden,
  setSensitiveHidden,
  getSensitiveMode,
  setSensitiveMode,
  maskData
}
